import { AddCircleOutline, GroupAdd } from '@mui/icons-material';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import {
    Box,
    Button,
    IconButton,
    Tabs,
    TextField,
    Typography,
    Alert
} from '@mui/material';
import React, { useState, useEffect } from 'react';
import { dark_red } from '../../../../utils/colors';
import { deleteDay, updateDay, getAllEvents, clearAllAssignments } from '../../../../utils/mutations/schedule';
import { Day, Event, Location, User, generateUUID } from '../../../../utils/types';
import DayTab from './DayTab';
import EventsTable from './EventsTable';
import BulkAssignmentDialog from './BulkAssignmentDialog';
import firebase from 'firebase/compat/app';
import 'firebase/compat/firestore';
import { db } from '../../../../utils/firebase';

// Add the Assignment interface
interface Assignment {
    email: string;
    eventIds: string[];
    eventNames?: string[];
    status?: 'valid' | 'invalid';
    error?: string;
}

interface ScheduleTabProps {
    confCode: string;
    users: User[];
    daysLocal: Day[];
    setDaysLocal: (days: Day[]) => void;
}

const ScheduleTab: React.FC<ScheduleTabProps> = ({ confCode, users, daysLocal, setDaysLocal }) => {
    const [selectedDayIndex, setSelectedDayIndex] = useState<number>(0);
    const [newDayDate, setNewDayDate] = useState<string>("");
    const [editingNewDayDate, setEditingNewDayDate] = useState<boolean>(false);
    const [bulkAssignmentOpen, setBulkAssignmentOpen] = useState(false);
    const [events, setEvents] = useState<Array<{ id: string; name: string }>>([]);
    const [error, setError] = useState<string | null>(null);

    const locations: Location[] = [...daysLocal.reduce((locationMap, day) => {
        // Function to add locations to the map
        const addLocations = (events: Event[]) => {
            events.forEach(event => {
                if (event.location && !locationMap.has(event.location.name)) {
                    locationMap.set(event.location.name, event.location);
                }
            });
        };
    
        // Add locations from events
        addLocations(day.events);
        
        // Add locations from deleted events
        if (day.deletedEvents) {
            addLocations(day.deletedEvents);
        }
        
        return locationMap;
    }, new Map<string, Location>()).values()];
    
    
    const handleChangeDate = (day: Day, newDate: string) => {
        // Implement rename functionality
        if (daysLocal.some(d => d.date === newDate)) {
            alert("Dates must be unique.");
            return;
        }

        const i = daysLocal.findIndex(d => d.date === day.date);
        daysLocal[i].date = newDate;
        daysLocal[i].isEditing = true;

        // Create a new array with the updated day
        const updatedDays = [...daysLocal];

        // Sort the new array
        const sortedDays = updatedDays.sort((a, b) => {
            if (!a.date || !b.date) return 0;

            const aDateParts = a.date.split("/");
            const bDateParts = b.date.split("/");
            const aMonth = parseInt(aDateParts[0]);
            const bMonth = parseInt(bDateParts[0]);
            const aDay = parseInt(aDateParts[1]);
            const bDay = parseInt(bDateParts[1]);

            // Compare months first
            if (aMonth !== bMonth) {
                return aMonth - bMonth;
            }

            // If months are the same, compare days
            return aDay - bDay;
        });

        // Find the index of the updated day in the sorted array
        const newIndex = sortedDays.findIndex(d => d.date === newDate);
        setSelectedDayIndex(newIndex);
        // Update the state with the sorted array and set selectedDayId to the id of the updated day
        setDaysLocal(sortedDays);
    };

    const handleDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const sanitizedText = event.target.value.replace(/[^0-9/]/g, '');
        setNewDayDate(sanitizedText);
    };  
    
    const handleDelete = (day: Day) => {
        const i = daysLocal.findIndex(d => d.id === day.id);
        daysLocal[i].isDeleted = !daysLocal[i].isDeleted;
        setDaysLocal([...daysLocal]);
    };

    const addDay = () => {
        // if string is not of the form ?[0-9][0-9]/[0-9][0-9]
        if (newDayDate === undefined || newDayDate === "") {
            setEditingNewDayDate(false);
            return;
        }

        if (!newDayDate.match(/^\d{0,2}\/\d{2}$/)) {
            alert("Please enter a valid date in the format mm/dd");
            return;
        }

        if (daysLocal.some(day => day.date === newDayDate)) {
            alert("Date already exists.");
            return;
        }

        const newUUID = generateUUID();

        // Add a new day to the schedule
        const newDay: Day = {
            id: newUUID,
            date: newDayDate,
            events: [],
            isEditing: true,
            isDeleted: false,
            deletedEvents: [],
        };

        // Create a new array with the added day
        const updatedDays = [...daysLocal, newDay];

        // Sort the new array
        const sortedDays = updatedDays.sort((a, b) => {
            if (!a.date || !b.date) return 0;

            const aDateParts = a.date.split("/");
            const bDateParts = b.date.split("/");
            const aMonth = parseInt(aDateParts[0]);
            const bMonth = parseInt(bDateParts[0]);
            const aDay = parseInt(aDateParts[1]);
            const bDay = parseInt(bDateParts[1]);

            // Compare months first
            if (aMonth !== bMonth) {
                return aMonth - bMonth;
            }

            // If months are the same, compare days
            return aDay - bDay;
        });

        // Update the state with the sorted array and set selectedDayId to the id of the newly added day
        setDaysLocal(sortedDays);

        setEditingNewDayDate(false);
    };

    const handlePublish = async () => {
        if (!daysLocal.some(day => day.isEditing || day.isDeleted)) {
            alert("No changes to publish.");
            return;
        }

        const input = prompt("Please enter password to publish changes:");
        if (input !== "admin123") {
            alert("Password is incorrect.");
            return;
        }

        // Create a new array to hold the modified days
        const updatedDays = [];

        // Publish the schedule
        for (const day of daysLocal) {
            if (day.isDeleted) {
                // Delete the day
                await deleteDay(confCode, day.id);
                continue;
            }

            if (day.isEditing) {
                // convert the DayLocal back to Day
                const dayToSend = {
                    id: day.id,
                    date: day.date,
                    events: day.events,
                };

                // Update the day
                await updateDay(confCode, dayToSend);

                // After pushing updates, set isEditing to false
                day.isEditing = false;
            }
            
            // Add the modified day to the updated array
            updatedDays.push(day);
        }
        // Update the state with the modified days
        setDaysLocal(updatedDays);

        window.sessionStorage.removeItem(`localDays_${confCode}`);

        window.location.reload();
    };

    // Define the handleAssignUsers function
    const handleAssignUsers = async (assignments: Assignment[]) => {
        try {
            if (!confCode) {
                throw new Error('Conference code not available');
            }

            // Validate assignments
            if (!assignments || assignments.length === 0) {
                throw new Error('No assignments provided');
            }

            console.log('Starting assignments with:', {
                conferenceCode: confCode,
                assignments: assignments.map(a => ({
                    email: a.email,
                    events: a.eventIds
                }))
            });

            // First verify all events exist and get their paths
            const daysRef = db.collection(`conferences/${confCode}/days`);
            const daysSnapshot = await daysRef.get();

            if (daysSnapshot.empty) {
                throw new Error('No days found for this conference');
            }

            // Build a map of event IDs to their full paths and day IDs
            const eventDayMap = new Map<string, { path: string; dayId: string }>();
            for (const dayDoc of daysSnapshot.docs) {
                const eventsRef = db.collection(`conferences/${confCode}/days/${dayDoc.id}/events`);
                const eventsSnapshot = await eventsRef.get();
                
                eventsSnapshot.docs.forEach(doc => {
                    eventDayMap.set(doc.id, {
                        path: `conferences/${confCode}/days/${dayDoc.id}/events/${doc.id}`,
                        dayId: dayDoc.id
                    });
                });
            }

            // Verify all events exist
            for (const assignment of assignments) {
                for (const eventId of assignment.eventIds) {
                    if (!eventDayMap.has(eventId)) {
                        throw new Error(`Event ${eventId} not found in conference ${confCode}`);
                    }
                    console.log(`Verified event exists: ${eventId}`);
                }
            }

            // Create a map to track which users need to be added to which events
            const userEventMap = new Map<string, Set<string>>();
            
            // Then process assignments
            for (const assignment of assignments) {
                console.log(`Processing assignment for email: ${assignment.email}`);
                
                // Find user
                const userQuery = await db
                    .collection('users')
                    .where('email', '==', assignment.email.toLowerCase())
                    .get();

                if (userQuery.empty) {
                    throw new Error(`User not found: ${assignment.email}`);
                }

                const uid = userQuery.docs[0].id;
                console.log(`Found user ${uid} for email ${assignment.email}`);

                // Track which events this user needs to be added to
                if (!userEventMap.has(uid)) {
                    userEventMap.set(uid, new Set());
                }
                
                // Add all event IDs to the user's set
                assignment.eventIds.forEach(eventId => {
                    userEventMap.get(uid)?.add(eventId);
                });
            }

            // Now update each event with all its assigned users
            for (const [eventId, eventInfo] of eventDayMap.entries()) {
                // Find all users that need to be added to this event
                const usersToAdd = Array.from(userEventMap.entries())
                    .filter(([_, eventIds]) => eventIds.has(eventId))
                    .map(([uid]) => uid);
                
                if (usersToAdd.length > 0) {
                    console.log(`Updating event ${eventId} with ${usersToAdd.length} users`);
                    
                    const eventRef = db.doc(eventInfo.path);
                    
                    try {
                        // Update the database
                        await eventRef.update({
                            assignedIDs: firebase.firestore.FieldValue.arrayUnion(...usersToAdd)
                        });
                        console.log(`Successfully updated event ${eventId} in database`);
                        
                        // Update local state
                        const dayIndex = daysLocal.findIndex(d => d.id === eventInfo.dayId);
                        if (dayIndex !== -1) {
                            const eventIndex = daysLocal[dayIndex].events.findIndex(e => e.id === eventId);
                            if (eventIndex !== -1) {
                                const event = daysLocal[dayIndex].events[eventIndex];
                                if (!event.assignedIDs) {
                                    event.assignedIDs = [];
                                }
                                
                                // Add all users that aren't already assigned
                                usersToAdd.forEach(uid => {
                                    if (!event.assignedIDs.includes(uid)) {
                                        event.assignedIDs.push(uid);
                                    }
                                });
                                
                                // Mark the day as edited to ensure changes are saved
                                daysLocal[dayIndex].isEditing = true;
                            }
                        }
                    } catch (updateError: any) {
                        console.error('Update failed:', {
                            eventId,
                            error: updateError.message,
                            code: updateError.code
                        });
                        throw new Error(`Failed to update event ${eventId}: ${updateError.message}`);
                    }
                }
            }
            
            // Force a re-render by creating a new array
            setDaysLocal([...daysLocal]);
            console.log('All assignments completed successfully and local state updated');

        } catch (error: any) {
            console.error('Assignment error:', {
                message: error.message,
                code: error.code,
                details: error
            });
            throw error;
        }
    };

    const handleBulkAssign = async (assignments: Assignment[]) => {
        try {
            if (!confCode) {
                throw new Error('Conference code not available');
            }

            console.log('Starting bulk assignment - clearing all prior assignments...');

            // Use the modular function to clear all assignments
            await clearAllAssignments(confCode);

            // Clear local state
            for(const day of daysLocal) {
                for(const event of day.events) {
                    event.assignedIDs = [];
                }
            }
            setDaysLocal([...daysLocal]);

            console.log('All prior assignments cleared, now assigning new users...');
            
            // Call the handleAssignUsers function
            await handleAssignUsers(assignments);
        } catch (error: any) {
            // Create a more specific error message
            const errorMessage = error.code === 'not-found'
                ? `Event or user not found: ${error.message}`
                : error.code === 'permission-denied'
                ? 'You do not have permission to update events'
                : `Failed to assign users: ${error.message}`;

            throw new Error(errorMessage);
        }
    };

    // Add this function to verify and refresh events
    const refreshEvents = async () => {
        try {
            if (!confCode) {
                throw new Error('No conference code available');
            }

            console.log('Checking events in conference days...');

            // Use the modular function to get all events
            const allEvents = await getAllEvents(confCode);

            console.log('Found events:', allEvents);

            if (allEvents.length === 0) {
                throw new Error(
                    'No events found in conference days. Checked path:\n' +
                    `- /conferences/${confCode}/days/*/events`
                );
            }

            setEvents(allEvents);
            
        } catch (error: any) {
            console.error('Failed to refresh events:', {
                error,
                conferenceCode: confCode,
                timestamp: new Date().toISOString()
            });
            setError(
                'Failed to load events. Please try refreshing the page. ' +
                'If the problem persists, contact support.'
            );
        }
    };

    // Call refreshEvents when the dialog opens
    const handleOpenBulkAssign = async () => {
        await refreshEvents(); // Refresh events before showing dialog
        setBulkAssignmentOpen(true);
    };

    useEffect(() => {
        // Extract all events from daysLocal
        const allEvents = daysLocal.flatMap(day => 
            day.events.map(event => ({
                id: event.id,
                name: event.name
            }))
        );
        setEvents(allEvents);
    }, [daysLocal]);

    return (
        <Box sx={{ display: 'flex', flexDirection: 'row', height: '100%' }}>
            {daysLocal.length === 0 ? (
                <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '10px',
                    padding: '20px',
                    border: '1px solid lightgrey',
                    borderRadius: '5px',
                    width: '50%',
                    margin: '20px auto',
                    '@media (max-width: 600px)': {
                        width: '100%',
                    },
                }}>
                    <CalendarTodayIcon sx={{ fontSize: 'large', color: dark_red }} />
                    <Typography variant="h6" sx={{ color: 'black' }}>No days added.</Typography>
                    {!editingNewDayDate ? (
                        <Button onClick={() => {
                            setNewDayDate("")
                            setEditingNewDayDate(true);
                        }}>
                            Add Day
                        </Button>
                    ) : (
                        <Box sx={{
                            display: 'flex',
                            flexDirection: 'row',
                        }}>
                            <TextField
                                value={newDayDate}
                                onChange={handleDateChange}
                                placeholder="mm/dd"
                                variant="standard"
                                inputProps={{
                                    maxLength: 5,
                                }}
                                sx={{
                                    width: "100px",
                                    alignSelf: "center",
                                }}
                            />
                            <Button onClick={addDay} variant="text">Add</Button>
                        </Box>
                    )}
                </Box>
            ) : (
                <>
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <Box sx={{ display: 'flex', gap: 1, marginBottom: '10px', justifyContent: 'center' }}>
                            <Button
                                onClick={handlePublish}
                                sx={{ variant: 'text' }}
                            >
                                Publish
                            </Button>
                            <IconButton
                                sx={{ color: dark_red }}
                                onClick={handleOpenBulkAssign}
                            >
                                <GroupAdd />
                            </IconButton>
                        </Box>
                        <Tabs
                            orientation="vertical"
                            variant="scrollable"
                            scrollButtons="auto"
                            allowScrollButtonsMobile
                            value={selectedDayIndex}
                            onChange={(_, newValue) => {
                                setSelectedDayIndex(newValue);
                            }}
                            sx={{ borderRight: 1, borderColor: 'divider', minWidth: '150px', maxHeight: 'calc(50px * 5)' }} // 48px is the approximate height of a single tab, adjust as needed
                        >
                            {daysLocal.map((day: Day) => (
                                <DayTab key={day.id} day={day} handleDelete={handleDelete} handleChangeDate={handleChangeDate} />
                            ))}
                        </Tabs>
                        {editingNewDayDate && (
                            <TextField
                                value={newDayDate}
                                placeholder="mm/dd"
                                variant="standard"
                                inputProps={{ maxLength: 5 }}
                                onChange={handleDateChange}
                                onBlur={addDay}
                                sx={{ width: "100px", alignSelf: "center" }}
                            />
                        )}
                        <IconButton
                            onClick={() => {
                                setNewDayDate("");
                                setEditingNewDayDate(true);
                            }}
                            disableFocusRipple
                            disableRipple
                            disableTouchRipple
                        >
                            <AddCircleOutline />
                        </IconButton>
                    </Box>
                    <Box sx={{ flexGrow: 1, p: 3 }}>
                        <EventsTable
                            selectedDayIndex={selectedDayIndex}
                            users={users}
                            locations={locations}
                            daysLocal={daysLocal}
                            setDaysLocal={setDaysLocal}
                        />
                    </Box>
                </>
            )}

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            <BulkAssignmentDialog
                open={bulkAssignmentOpen}
                onClose={() => setBulkAssignmentOpen(false)}
                onAssign={handleBulkAssign}
                events={events}
            />
        </Box>
    );
};

export default ScheduleTab;
